import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Progress } from '@/components/ui/progress';
import { TileEmpty } from '../TileWrapper';
import { BarChart3, Circle, CircleDot, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  todo: 12,
  in_progress: 8,
  done: 25,
  total: 45,
  percentage: 56,
};

export function TaskProgressTile({ filter, size, useMockData }) {
  const { company } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        let query = supabase
          .from('tasks')
          .select('status, projects!inner(company_id)')
          .eq('projects.company_id', company.id);

        if (filter?.spaceId) {
          query = query.eq('project_id', filter.spaceId);
        }

        const { data: tasks, error } = await query;

        if (error) throw error;

        const counts = {
          todo: 0,
          in_progress: 0,
          done: 0,
        };

        (tasks || []).forEach(t => {
          if (counts.hasOwnProperty(t.status)) {
            counts[t.status]++;
          } else {
            counts.todo++;
          }
        });

        const total = counts.todo + counts.in_progress + counts.done;
        const percentage = total > 0 ? Math.round((counts.done / total) * 100) : 0;

        setData({
          ...counts,
          total,
          percentage,
        });
      } catch (err) {
        console.error('Failed to load task progress:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, filter?.spaceId, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse flex gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="text-center">
              <div className="w-16 h-20 bg-gray-200 rounded mb-2" />
              <div className="h-3 w-12 bg-gray-200 rounded mx-auto" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return <TileEmpty message="No tasks found" icon={BarChart3} />;
  }

  const isLarge = size === 'large';

  const columns = [
    {
      key: 'todo',
      label: 'To Do',
      count: data.todo,
      icon: Circle,
      color: 'text-gray-500',
      bgColor: 'bg-gray-100',
      barColor: 'bg-gray-400',
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      count: data.in_progress,
      icon: CircleDot,
      color: 'text-blue-500',
      bgColor: 'bg-blue-100',
      barColor: 'bg-blue-500',
    },
    {
      key: 'done',
      label: 'Done',
      count: data.done,
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-100',
      barColor: 'bg-green-500',
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Kanban columns */}
      <div className="flex-1 flex gap-3">
        {columns.map((col) => {
          const Icon = col.icon;
          const heightPct = data.total > 0 ? (col.count / data.total) * 100 : 0;

          return (
            <div key={col.key} className="flex-1 flex flex-col items-center">
              <div
                className={cn(
                  "w-full rounded-lg flex flex-col items-center justify-end p-3",
                  col.bgColor,
                  isLarge ? "min-h-[120px]" : "min-h-[80px]"
                )}
                style={{ height: `${Math.max(heightPct, 30)}%` }}
              >
                <Icon className={cn("h-5 w-5 mb-1", col.color)} />
                <span className="text-2xl font-bold">{col.count}</span>
              </div>
              <p className="text-xs text-gray-600 mt-2 text-center">{col.label}</p>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-4 pt-3 border-t">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-600">Completion</span>
          <span className="text-sm font-medium">{data.percentage}%</span>
        </div>
        <div className="flex h-2 rounded-full overflow-hidden bg-gray-100">
          <div
            className="bg-green-500 transition-all"
            style={{ width: `${(data.done / data.total) * 100}%` }}
          />
          <div
            className="bg-blue-500 transition-all"
            style={{ width: `${(data.in_progress / data.total) * 100}%` }}
          />
        </div>
        {isLarge && (
          <p className="text-xs text-center text-gray-500 mt-2">
            {data.total} total tasks
          </p>
        )}
      </div>
    </div>
  );
}
